import React from "react";
import { Box, Typography, TextField, IconButton } from "@mui/material";
import DeleteIcon from "@mui/icons-material/Delete";
import PushPinIcon from "@mui/icons-material/PushPin";
import VolumeOffIcon from "@mui/icons-material/VolumeOff";
import VolumeUpIcon from "@mui/icons-material/VolumeUp";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";

const TELEGRAM_BLUE = "#2AABEE";

function TelegramSvgIcon({ size = 18 }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" style={{ flexShrink: 0 }}>
      <circle cx="12" cy="12" r="12" fill={TELEGRAM_BLUE} />
      <path
        d="M5.2 11.6l13.1-5c.6-.2 1.1.1.9.8l-2.2 10.4c-.2.7-.7.9-1.2.6l-3.2-2.4-1.5 1.5c-.2.2-.4.3-.7.3l.2-3.4 5.7-5.2c.2-.2 0-.4-.3-.2l-7.1 4.5-3-1c-.7-.2-.7-.7.3-1z"
        fill="white"
      />
    </svg>
  );
}

function SectionLabel({ children }) {
  return (
    <Typography
      sx={{
        fontSize: "11px",
        fontWeight: 600,
        color: "var(--text-color)",
        opacity: 0.55,
        mb: "8px",
        textTransform: "uppercase",
        letterSpacing: "0.6px",
      }}>
      {children}
    </Typography>
  );
}

function PillOption({ label, Icon, selected, onClick }) {
  return (
    <Box
      onClick={onClick}
      sx={{
        display: "flex",
        alignItems: "center",
        gap: "5px",
        padding: "6px 12px",
        borderRadius: "20px",
        cursor: "pointer",
        border: selected
          ? `1px solid ${TELEGRAM_BLUE}`
          : "1px solid var(--border-color)",
        backgroundColor: selected ? "rgba(42,171,238,0.1)" : "transparent",
        color: selected ? TELEGRAM_BLUE : "var(--text-color)",
        opacity: selected ? 1 : 0.55,
        fontSize: "12px",
        fontWeight: selected ? 600 : 400,
        transition: "all 0.15s",
        userSelect: "none",
        "&:hover": { opacity: 1, borderColor: TELEGRAM_BLUE, color: TELEGRAM_BLUE },
      }}>
      {Icon && <Icon sx={{ fontSize: 13 }} />}
      {label}
    </Box>
  );
}

const fieldSx = {
  "& .MuiOutlinedInput-root": {
    fontSize: "12px",
    borderRadius: "6px",
    "& fieldset": { borderColor: "var(--border-color)" },
    "&:hover fieldset": { borderColor: TELEGRAM_BLUE },
    "&.Mui-focused fieldset": { borderColor: TELEGRAM_BLUE },
  },
  "& .MuiInputBase-input": {
    color: "var(--text-color)",
    padding: "5px 10px",
  },
  "& .MuiInputBase-input::placeholder": { opacity: 0.4 },
};

function ButtonEditor({ button, onChange, onDelete }) {
  const [mode, setMode] = React.useState(button.callback_data ? "callback" : "url");

  const handleModeToggle = (m) => {
    setMode(m);
    if (m === "url") {
      onChange({ text: button.text || "", url: button.url || button.callback_data || "", callback_data: undefined });
    } else {
      onChange({ text: button.text || "", callback_data: button.callback_data || button.url || "", url: undefined });
    }
  };

  return (
    <Box sx={{ display: "flex", gap: "6px", alignItems: "flex-start", mb: "6px" }}>
      <Box sx={{ flex: 1 }}>
        <TextField
          placeholder="Button text"
          size="small"
          fullWidth
          value={button.text || ""}
          onChange={(e) => onChange({ ...button, text: e.target.value })}
          sx={{ ...fieldSx, mb: "4px" }}
        />
        <Box sx={{ display: "flex", gap: "4px" }}>
          {/* URL / Callback toggle */}
          <Box
            sx={{
              display: "flex",
              borderRadius: "6px",
              border: "1px solid var(--border-color)",
              overflow: "hidden",
              flexShrink: 0,
            }}>
            {[
              { key: "url", label: "URL" },
              { key: "callback", label: "CB" },
            ].map(({ key, label }) => (
              <Box
                key={key}
                onClick={() => handleModeToggle(key)}
                sx={{
                  px: "8px",
                  py: "4px",
                  cursor: "pointer",
                  fontSize: "10px",
                  fontWeight: 600,
                  lineHeight: "14px",
                  backgroundColor: mode === key ? TELEGRAM_BLUE : "transparent",
                  color: mode === key ? "#fff" : "var(--text-color)",
                  opacity: mode === key ? 1 : 0.45,
                  transition: "all 0.15s",
                  userSelect: "none",
                }}>
                {label}
              </Box>
            ))}
          </Box>
          <TextField
            placeholder={mode === "url" ? "https://example.com" : "callback_data"}
            size="small"
            fullWidth
            value={mode === "url" ? (button.url || "") : (button.callback_data || "")}
            onChange={(e) => {
              if (mode === "url") {
                onChange({ ...button, url: e.target.value, callback_data: undefined });
              } else {
                onChange({ ...button, callback_data: e.target.value, url: undefined });
              }
            }}
            sx={fieldSx}
          />
        </Box>
      </Box>
      <IconButton
        onClick={onDelete}
        size="small"
        sx={{
          color: "var(--text-color)",
          opacity: 0.35,
          mt: "1px",
          "&:hover": { opacity: 1, color: "error.main" },
        }}>
        <DeleteIcon sx={{ fontSize: 15 }} />
      </IconButton>
    </Box>
  );
}

function KeyboardRow({ row, rowIdx, onChange, onAddButton, onDeleteButton, onDeleteRow }) {
  return (
    <Box
      sx={{
        mb: "8px",
        border: "1px solid var(--border-color)",
        borderRadius: "8px",
        padding: "10px 12px",
      }}>
      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          mb: row.length > 0 ? "8px" : "0",
        }}>
        <Typography
          sx={{
            fontSize: "11px",
            fontWeight: 600,
            color: "var(--text-color)",
            opacity: 0.4,
          }}>
          Row {rowIdx + 1}
        </Typography>
        <Box sx={{ display: "flex", gap: "4px", alignItems: "center" }}>
          <Box
            onClick={onAddButton}
            sx={{
              display: "flex",
              alignItems: "center",
              fontSize: "11px",
              cursor: "pointer",
              color: TELEGRAM_BLUE,
              border: `1px solid ${TELEGRAM_BLUE}`,
              borderRadius: "12px",
              padding: "2px 8px",
              opacity: 0.8,
              "&:hover": { opacity: 1 },
            }}>
            + Button
          </Box>
          <IconButton
            onClick={onDeleteRow}
            size="small"
            sx={{
              color: "var(--text-color)",
              opacity: 0.3,
              "&:hover": { opacity: 1, color: "error.main" },
            }}>
            <DeleteIcon sx={{ fontSize: 14 }} />
          </IconButton>
        </Box>
      </Box>
      {row.map((button, btnIdx) => (
        <ButtonEditor
          key={btnIdx}
          button={button}
          onChange={(btn) => onChange(btnIdx, btn)}
          onDelete={() => onDeleteButton(btnIdx)}
        />
      ))}
      {row.length === 0 && (
        <Typography
          sx={{
            fontSize: "11px",
            color: "var(--text-color)",
            opacity: 0.35,
            textAlign: "center",
            py: "4px",
          }}>
          Click "+ Button" to add buttons to this row
        </Typography>
      )}
    </Box>
  );
}

const TelegramSettings = ({
  pinMessage = false,
  pinSilent = false,
  inlineKeyboard = [],
  onChange,
}) => {
  const [pin_message, setPinMessage] = React.useState(!!pinMessage);
  const [pin_silent, setPinSilent] = React.useState(!!pinSilent);
  const [inline_keyboard, setInlineKeyboard] = React.useState(
    inlineKeyboard.length > 0 ? inlineKeyboard : [],
  );

  React.useEffect(() => {
    if (onChange) {
      const cleaned = inline_keyboard
        .map((row) => row.filter((btn) => btn.text && btn.text.trim()))
        .filter((row) => row.length > 0);
      onChange({ pin_message, pin_silent, inline_keyboard: cleaned });
    }
  }, [pin_message, pin_silent, inline_keyboard, onChange]);

  const handleAddRow = () => {
    if (inline_keyboard.length < 10) setInlineKeyboard([...inline_keyboard, []]);
  };

  const handleDeleteRow = (idx) =>
    setInlineKeyboard(inline_keyboard.filter((_, i) => i !== idx));

  const handleAddButton = (rowIdx) =>
    setInlineKeyboard(
      inline_keyboard.map((row, i) =>
        i === rowIdx ? [...row, { text: "", url: "" }] : row,
      ),
    );

  const handleDeleteButton = (rowIdx, btnIdx) =>
    setInlineKeyboard(
      inline_keyboard.map((row, i) =>
        i === rowIdx ? row.filter((_, j) => j !== btnIdx) : row,
      ),
    );

  const handleChangeButton = (rowIdx, btnIdx, btn) =>
    setInlineKeyboard(
      inline_keyboard.map((row, i) =>
        i === rowIdx ? row.map((b, j) => (j === btnIdx ? btn : b)) : row,
      ),
    );

  return (
    <Box className="post-card post-card-sm">
      {/* Header */}
      <Box className="post-card-header post-card-header-sm" sx={{ gap: "8px" }}>
        <TelegramSvgIcon size={18} />
        <Typography className="post-card-title">Telegram Settings</Typography>
      </Box>

      <Box
        sx={{
          padding: "16px 20px",
          display: "flex",
          flexDirection: "column",
          gap: "16px",
        }}>
        {/* Pin Message */}
        <Box>
          <SectionLabel>Pin Message</SectionLabel>
          <Box sx={{ display: "flex", gap: "8px" }}>
            <PillOption
              label="Don't pin"
              selected={!pin_message}
              onClick={() => setPinMessage(false)}
            />
            <PillOption
              label="Pin"
              Icon={PushPinIcon}
              selected={pin_message}
              onClick={() => setPinMessage(true)}
            />
          </Box>
        </Box>

        {/* Notification (only when pinning) */}
        {pin_message && (
          <Box>
            <SectionLabel>Notification</SectionLabel>
            <Box sx={{ display: "flex", gap: "8px" }}>
              <PillOption
                label="With sound"
                Icon={VolumeUpIcon}
                selected={!pin_silent}
                onClick={() => setPinSilent(false)}
              />
              <PillOption
                label="Silent"
                Icon={VolumeOffIcon}
                selected={pin_silent}
                onClick={() => setPinSilent(true)}
              />
            </Box>
          </Box>
        )}

        {/* Inline Keyboard */}
        <Box>
          <Box
            sx={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              mb: "10px",
            }}>
            <Typography
              sx={{
                fontSize: "11px",
                fontWeight: 600,
                color: "var(--text-color)",
                opacity: 0.55,
                textTransform: "uppercase",
                letterSpacing: "0.6px",
              }}>
              Inline Keyboard
            </Typography>
            {inline_keyboard.length < 10 && (
              <Box
                onClick={handleAddRow}
                sx={{
                  fontSize: "11px",
                  cursor: "pointer",
                  color: TELEGRAM_BLUE,
                  border: `1px solid ${TELEGRAM_BLUE}`,
                  borderRadius: "12px",
                  padding: "2px 10px",
                  opacity: 0.8,
                  "&:hover": { opacity: 1 },
                }}>
                + Add Row
              </Box>
            )}
          </Box>

          {inline_keyboard.length === 0 ? (
            <Box
              sx={{
                border: "1px dashed var(--border-color)",
                borderRadius: "8px",
                padding: "16px",
                textAlign: "center",
              }}>
              <Typography
                sx={{
                  fontSize: "12px",
                  color: "var(--text-color)",
                  opacity: 0.4,
                  lineHeight: 1.6,
                }}>
                No inline keyboard set.
                <br />
                Add rows to create buttons below your post.
              </Typography>
            </Box>
          ) : (
            inline_keyboard.map((row, rowIdx) => (
              <KeyboardRow
                key={rowIdx}
                row={row}
                rowIdx={rowIdx}
                onChange={(btnIdx, btn) => handleChangeButton(rowIdx, btnIdx, btn)}
                onAddButton={() => handleAddButton(rowIdx)}
                onDeleteButton={(btnIdx) => handleDeleteButton(rowIdx, btnIdx)}
                onDeleteRow={() => handleDeleteRow(rowIdx)}
              />
            ))
          )}
        </Box>

        {/* Info note */}
        <Box sx={{ display: "flex", alignItems: "flex-start", gap: "7px" }}>
          <InfoOutlinedIcon
            sx={{
              fontSize: 13,
              color: "var(--text-color)",
              opacity: 0.35,
              mt: "2px",
              flexShrink: 0,
            }}
          />
          <Typography
            sx={{
              fontSize: "11px",
              color: "var(--text-color)",
              opacity: 0.4,
              lineHeight: 1.55,
            }}>
            Inline buttons appear below the post. Pinning requires admin rights in the
            channel.
          </Typography>
        </Box>
      </Box>
    </Box>
  );
};

export default TelegramSettings;
